/**
 * A checkout refused for what the basket holds — a request-to-book ticket
 * with other items, a booking that mixes request-to-book and other tickets
 * on its own, a school that takes one item at a time — rather than for the
 * buyer or the payment. Its fix is to remove items (the other items, or the
 * booking to book again apart), so the checkouts (the widget's
 * /basket/checkout, the webcomponents basket checkout) say how and offer the
 * way into the basket; and once the buyer changes the basket the refusal is
 * spent. One rule for both.
 */

const BASKET_CONTENTS_ERROR_CODES: ReadonlySet<string> = new Set([
  "REQUEST_TO_BOOK_MIXED_BASKET",
  "REQUEST_TO_BOOK_MIXED_BOOKING",
  "HOLD_FLOW_SINGLE_ITEM_ONLY",
  "BASKET_DISABLED_SINGLE_ITEM_ONLY",
]);

/** What a checkout says beside a refusal of the basket's contents where its
 *  order summary has a remove × (drawer or not; the widget's request step
 *  has none). */
export const REMOVE_FROM_SUMMARY_HINT =
  "You can remove an item here with the × beside it in the order summary.";

/** Whether a checkout error `code` is fixed by changing the basket's items. */
export function isBasketContentsError(code: string | null | undefined): boolean {
  return !!code && BASKET_CONTENTS_ERROR_CODES.has(code);
}

/**
 * Which items a basket holds, and nothing else: what a basket-contents
 * refusal was judged on. Adding or removing an item moves it; quantity edits,
 * revalidation and the store re-emitting the same items in another order
 * don't (none of them can fix such a refusal).
 */
export function basketItemIdsKey(items: ReadonlyArray<{ id: string }>): string {
  return items
    .map((item) => item.id)
    .sort()
    .join("\n");
}

/** A refusal of the basket's contents: its message, and the items it was
 *  judged on. */
export type ContentsRefusal = { message: string; judgedItemIds: string };

/** What to remember of a failed checkout press: a refusal only for a
 *  contents `code`, judged on the items the request was built from. */
export function contentsRefusalFor(
  message: string,
  code: unknown,
  judgedItems: ReadonlyArray<{ id: string }>,
): ContentsRefusal | null {
  if (typeof code !== "string" || !isBasketContentsError(code)) return null;
  return { message, judgedItemIds: basketItemIdsKey(judgedItems) };
}

/**
 * Whether the buyer has since added or removed an item, so the refusal no
 * longer describes this basket — including one that landed after the change
 * — and the next press is to be judged afresh: a checkout stops showing it.
 */
export function contentsRefusalSpent(
  refusal: ContentsRefusal | null,
  items: ReadonlyArray<{ id: string }>,
): boolean {
  return refusal !== null && refusal.judgedItemIds !== basketItemIdsKey(items);
}
